import type {
  AdminSponsorshipApplicationPage,
  AdminSponsorshipPage,
  AdminUserDetailResponse,
  AdminUserVerifiedChannel,
} from '@inflip/contracts/admin';
import {
  IP_RIGHT_LABELS,
  ORDER_STATUS_LABELS,
  SOCIAL_PLATFORM_LABELS,
  UserRole,
  type SocialPlatform,
} from '@inflip/shared';
import { Link, useNavigate, useParams, useSearch } from '@tanstack/react-router';
import { useCallback, useEffect, useState } from 'react';

import { BackLink } from '../components/back-link';
import { OwnedSponsorships } from '../components/owned-sponsorships';
import {
  qualifiedChannelLabel,
  SponsorshipTitleCell,
} from '../components/sponsorship-application-cells';
import {
  Button,
  ConfirmDialog,
  ErrorBanner,
  InfoRow,
  LoadingRow,
  PromptDialog,
  StatTile,
} from '../components/ui';
import { api } from '../lib/api-client';
import { useAdminAuth } from '../lib/auth-context';
import {
  LISTING_STATUS_META,
  ORDER_STATUS_TEXT,
  SPONSORSHIP_APPLICATION_STATUS_META,
} from '../lib/status-ui';
import { formatDate, formatDateTime, formatTime } from '../lib/time-zone';

const PROVIDER_LABELS: Record<AdminUserDetailResponse['provider'], string> = {
  LOCAL: '이메일',
  GOOGLE: '구글',
  APPLE: '애플',
  KAKAO: '카카오',
};

const TAB_KEYS = [
  'account',
  'orders',
  'listings',
  'ownedSponsorships',
  'sponsorships',
  'agreements',
] as const;
/** 회원 상세의 협찬 지원 — 최근 50건, 더 있으면 협찬 지원 내역 화면으로 */
const SPONSORSHIP_APPLICATION_LIMIT = 50;
type TabKey = (typeof TAB_KEYS)[number];

export function UserDetailPage() {
  const { userId } = useParams({ from: '/users/$userId' });
  const search = useSearch({ from: '/users/$userId' });
  const navigate = useNavigate();
  const tab: TabKey = TAB_KEYS.includes(search.tab as TabKey) ? (search.tab as TabKey) : 'account';

  const { session } = useAdminAuth();
  const [user, setUser] = useState<AdminUserDetailResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [suspendOpen, setSuspendOpen] = useState(false);
  const [revoking, setRevoking] = useState<AdminUserVerifiedChannel | null>(null);
  const [busy, setBusy] = useState(false);
  const [pinResetOpen, setPinResetOpen] = useState(false);
  const [applications, setApplications] = useState<AdminSponsorshipApplicationPage | null>(null);
  const [ownedSponsorships, setOwnedSponsorships] = useState<AdminSponsorshipPage | null>(null);

  const reload = useCallback(() => {
    if (!session) return Promise.resolve();
    return api
      .getUser(session.accessToken, userId)
      .then(setUser)
      .catch((err) => setError(err instanceof Error ? err.message : String(err)));
  }, [session, userId]);

  useEffect(() => {
    void reload();
  }, [reload]);

  // 협찬 지원 — 탭 개수에도 쓰여서 처음에 함께 불러온다
  useEffect(() => {
    if (!session) return;
    let cancelled = false;
    api
      .listSponsorshipApplications(session.accessToken, {
        applicantId: userId,
        pageSize: SPONSORSHIP_APPLICATION_LIMIT,
      })
      .then((res) => {
        if (!cancelled) setApplications(res);
      })
      .catch(() => undefined);
    // 이 회원이 등록한 협찬 — 상태 전체
    api
      .listSponsorships(session.accessToken, { ownerId: userId, pageSize: 100 })
      .then((res) => {
        if (!cancelled) setOwnedSponsorships(res);
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [session, userId]);

  if (!session) return null;

  const setTab = (next: TabKey) =>
    void navigate({
      to: '/users/$userId',
      params: { userId },
      search: next === 'account' ? {} : { tab: next },
      replace: true,
    });

  const suspend = async (reason: string) => {
    if (busy) return;
    setBusy(true);
    setActionError(null);
    try {
      await api.suspendUser(session.accessToken, userId, { reason });
      setSuspendOpen(false);
      await reload();
    } catch (err) {
      setActionError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  };

  const revokeChannel = async (channel: AdminUserVerifiedChannel, reason: string) => {
    if (busy) return;
    setBusy(true);
    setActionError(null);
    try {
      await api.revokeSocialChannel(session.accessToken, userId, channel.platform, {
        reason,
        verifiedAt: channel.verifiedAt,
      });
      setRevoking(null);
      await reload();
    } catch (err) {
      setActionError(err instanceof Error ? err.message : String(err));
      // 그사이 다시 인증했을 수 있다 — 지금 채널로 다시 그린다
      setRevoking(null);
      await reload();
    } finally {
      setBusy(false);
    }
  };

  const resetSimplePin = async () => {
    if (busy) return;
    setBusy(true);
    setActionError(null);
    try {
      await api.resetUserSimplePin(session.accessToken, userId);
      setPinResetOpen(false);
      await reload();
    } catch (err) {
      setActionError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  };

  /** 운영 계정으로 웹 열기 — 팝업 차단을 피하려고 창을 먼저 열고 주소를 받아 넘긴다 */
  const openAsOperator = async () => {
    if (busy) return;
    const win = window.open('', '_blank');
    setBusy(true);
    setActionError(null);
    try {
      const { url } = await api.operatorSignIn(session.accessToken, userId);
      if (win) {
        win.opener = null;
        win.location.href = url;
      } else {
        window.location.href = url;
      }
    } catch (err) {
      win?.close();
      setActionError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  };

  const unsuspend = async () => {
    if (!user?.suspendedAt || busy) return;
    setBusy(true);
    setActionError(null);
    try {
      await api.unsuspendUser(session.accessToken, userId, {
        seenSuspendedAt: user.suspendedAt,
        seenSuspendedReason: user.suspendedReason,
      });
      await reload();
    } catch (err) {
      setActionError(err instanceof Error ? err.message : String(err));
      await reload();
    } finally {
      setBusy(false);
    }
  };

  // 인증된 플랫폼은 인증 채널에 나오므로, 입력만 하고 인증하지 않은 링크만 따로 보여준다
  const unverifiedLinks = Object.entries(user?.socialLinks ?? {}).filter(
    ([platform]) => !user?.verifiedChannels.some((c) => c.platform === platform),
  );

  const tabs: { key: TabKey; label: string; count?: number }[] = user
    ? [
        { key: 'account', label: '계정' },
        { key: 'orders', label: '주문', count: user.orderCount },
        { key: 'listings', label: '판매 상품', count: user.listingCount },
        { key: 'ownedSponsorships', label: '등록한 협찬', count: ownedSponsorships?.total },
        { key: 'sponsorships', label: '협찬 지원', count: applications?.total },
        { key: 'agreements', label: '약관 동의', count: user.agreements.length },
      ]
    : [];

  return (
    <main className="mx-auto w-full max-w-[720px] px-6 py-8">
      <div className="mb-6">
        <BackLink
          fallback="/users"
          fallbackLabel="회원 목록"
          className="text-sm font-medium text-muted-foreground hover:text-brand"
        />
      </div>

      {error && <ErrorBanner message={error} />}
      {!user && !error && <LoadingRow />}

      {user && (
        <>
          {/* ── 프로필 헤더 ── */}
          <header className="flex items-start gap-4">
            {user.avatarUrl ? (
              <img src={user.avatarUrl} alt="" className="h-14 w-14 rounded-full object-cover" />
            ) : (
              <span className="grid h-14 w-14 place-items-center rounded-full bg-muted text-xl font-extrabold text-muted-foreground">
                {user.name.slice(0, 1)}
              </span>
            )}
            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-center gap-2">
                <h1 className="text-2xl font-extrabold tracking-tight">{user.name}</h1>
                {user.suspendedAt && (
                  <span className="text-sm font-bold text-rose-600">이용 정지</span>
                )}
                {user.role !== UserRole.USER && (
                  <span className="text-sm font-bold text-brand">{user.role}</span>
                )}
                {user.isOperatorAccount && (
                  <span className="text-sm font-bold text-brand">운영 계정</span>
                )}
              </div>
              <p className="mt-0.5 truncate text-sm text-muted-foreground">{user.email}</p>
              {user.operatorMemo && (
                <p className="mt-0.5 text-sm text-muted-foreground">{user.operatorMemo}</p>
              )}
              <CopyableId value={user.id} />
            </div>
            {user.isOperatorAccount && !user.suspendedAt && !user.withdrawnAt && (
              <Button variant="secondary" disabled={busy} onClick={() => void openAsOperator()}>
                이 계정으로 웹 열기
              </Button>
            )}
            {user.role === UserRole.USER &&
              (user.suspendedAt ? (
                <Button variant="secondary" disabled={busy} onClick={() => void unsuspend()}>
                  정지 해제
                </Button>
              ) : (
                <Button variant="danger" disabled={busy} onClick={() => setSuspendOpen(true)}>
                  이용 정지
                </Button>
              ))}
          </header>

          {actionError && (
            <div className="mt-4">
              <ErrorBanner message={actionError} />
            </div>
          )}

          {user.suspendedAt && (
            <div className="mt-5 rounded-lg bg-rose-50 px-5 py-4 text-sm text-rose-700">
              <span className="font-bold">{formatDateTime(user.suspendedAt)}</span>
              부터 이용이 정지된 회원이에요.
              {user.suspendedReason && (
                <span className="mt-0.5 block">사유: {user.suspendedReason}</span>
              )}
            </div>
          )}

          {/* ── 활동 요약 타일 ── */}
          <div className="mt-6 grid grid-cols-3 gap-3">
            <StatTile label="주문" value={`${user.orderCount}건`} />
            <StatTile
              label="구매 총액"
              value={`${user.totalPurchaseAmount.toLocaleString('ko-KR')}원`}
            />
            <StatTile label="판매 상품" value={`${user.listingCount}건`} />
          </div>

          {/* ── 탭 바 ── */}
          <nav className="mt-8 flex gap-6 border-b border-border">
            {tabs.map((t) => (
              <button
                key={t.key}
                type="button"
                onClick={() => setTab(t.key)}
                className={
                  tab === t.key
                    ? '-mb-px border-b-2 border-foreground pb-2.5 text-sm font-bold text-foreground'
                    : 'pb-2.5 text-sm font-semibold text-muted-foreground transition hover:text-foreground'
                }
              >
                {t.label}
                {t.count !== undefined && (
                  <span
                    className={`ml-1 tabular-nums ${tab === t.key ? 'text-brand' : 'text-muted-foreground/70'}`}
                  >
                    {t.count}
                  </span>
                )}
              </button>
            ))}
          </nav>

          {/* ── 탭 콘텐츠 ── */}
          <div className="mt-2">
            {tab === 'account' && (
              <div className="divide-y divide-border/60">
                <InfoRow label="가입 경로">
                  {PROVIDER_LABELS[user.provider]}
                  {user.emailVerified ? ' · 이메일 인증 완료' : ' · 이메일 미인증'}
                  {user.mfaEnrolled ? ' · MFA' : ''}
                </InfoRow>
                <InfoRow label="연락처">
                  {user.phoneNumber ? `${user.phoneCountryCode ?? ''} ${user.phoneNumber}` : '—'}
                </InfoRow>
                <InfoRow label="정산 계좌">
                  {user.payoutAccount
                    ? `${user.payoutAccount.bankName} ${user.payoutAccount.accountNumber} (${user.payoutAccount.holderName})`
                    : '—'}
                </InfoRow>
                <InfoRow label="환불 계좌">
                  {user.refundAccount ? (
                    `${user.refundAccount.bankName} ${user.refundAccount.accountNumber} (${user.refundAccount.holderName})`
                  ) : user.refundAccountRequired ? (
                    <span className="text-amber-700">미등록 · 구매 신청 있음</span>
                  ) : (
                    '—'
                  )}
                </InfoRow>
                <InfoRow label="간편비밀번호">
                  <span className="flex flex-wrap items-center justify-end gap-x-3 gap-y-1">
                    {!user.hasSimplePin ? (
                      <span className="text-muted-foreground">미설정</span>
                    ) : user.simplePinLockedUntil ? (
                      <span className="font-semibold text-rose-600">
                        잠김 · {formatTime(user.simplePinLockedUntil)}까지
                      </span>
                    ) : (
                      <span className="font-semibold text-emerald-600">설정됨</span>
                    )}
                    {user.hasSimplePin && user.role === UserRole.USER && (
                      <button
                        type="button"
                        disabled={busy}
                        onClick={() => setPinResetOpen(true)}
                        className="text-sm font-semibold text-muted-foreground underline underline-offset-2 transition hover:text-rose-600 disabled:opacity-40"
                      >
                        초기화
                      </button>
                    )}
                  </span>
                </InfoRow>
                <InfoRow label="가입일">{formatDateTime(user.createdAt)}</InfoRow>
                <InfoRow label="최근 로그인">
                  {user.lastLoginAt ? formatDateTime(user.lastLoginAt) : '—'}
                </InfoRow>
                <InfoRow label="인증 채널">
                  {user.verifiedChannels.length > 0 ? (
                    <span className="flex flex-col items-end gap-1.5">
                      {user.verifiedChannels.map((c) => (
                        <span key={c.platform} className="flex flex-wrap justify-end gap-x-2">
                          <a
                            href={c.profileUrl}
                            target="_blank"
                            rel="noreferrer"
                            title={c.profileUrl}
                            className="font-semibold text-brand hover:underline"
                          >
                            {SOCIAL_PLATFORM_LABELS[c.platform]}
                            {c.username
                              ? ` @${c.username.replace(/^@/, '')}`
                              : c.displayName
                                ? ` ${c.displayName}`
                                : ''}{' '}
                            ↗
                          </a>
                          <span className="tabular-nums">
                            {followerLabel(c.platform)}{' '}
                            {c.followerCount === null
                              ? '비공개'
                              : `${c.followerCount.toLocaleString('ko-KR')}명`}
                          </span>
                          <span className="text-xs font-normal text-muted-foreground">
                            {formatDate(c.verifiedAt)} 인증 시점
                          </span>
                          <button
                            type="button"
                            disabled={busy}
                            onClick={() => setRevoking(c)}
                            className="text-xs font-semibold text-rose-600 hover:underline disabled:opacity-50"
                          >
                            인증 해제
                          </button>
                        </span>
                      ))}
                    </span>
                  ) : (
                    '—'
                  )}
                </InfoRow>
                <InfoRow label="등록한 링크">
                  {unverifiedLinks.length > 0 ? (
                    <span className="flex flex-wrap justify-end gap-x-3 gap-y-1">
                      {unverifiedLinks.map(([k, url]) => (
                        <a
                          key={k}
                          href={url}
                          target="_blank"
                          rel="noreferrer"
                          title={url}
                          className="font-semibold text-muted-foreground hover:underline"
                        >
                          {SOCIAL_PLATFORM_LABELS[k as SocialPlatform] ?? k} ↗
                        </a>
                      ))}
                      <span className="text-xs font-normal text-muted-foreground">미인증</span>
                    </span>
                  ) : (
                    '—'
                  )}
                </InfoRow>
              </div>
            )}

            {tab === 'orders' &&
              (user.recentOrders.length === 0 ? (
                <EmptyLine text="주문이 없어요." />
              ) : (
                <>
                  <ul className="-mx-2 divide-y divide-border/40">
                    {user.recentOrders.map((o) => (
                      <li key={o.id}>
                        <Link
                          to="/orders/$orderId"
                          params={{ orderId: o.id }}
                          className="flex items-center gap-3 rounded-md px-2 py-3 transition hover:bg-muted"
                        >
                          <div className="min-w-0 flex-1">
                            <p className="truncate text-sm font-bold">
                              {o.listingTitle ?? '(삭제된 상품)'}
                              <span className="ml-1.5 font-medium text-muted-foreground">
                                {IP_RIGHT_LABELS[o.rightType]}
                              </span>
                            </p>
                            <p className="mt-0.5 text-xs text-muted-foreground">
                              {formatDate(o.createdAt)} · #{o.id.slice(0, 8)}
                            </p>
                          </div>
                          <div className="shrink-0 text-right">
                            <p className="text-sm font-bold tabular-nums">
                              {o.totalAmount.toLocaleString('ko-KR')}원
                            </p>
                            <p
                              className={`mt-0.5 text-xs font-semibold ${ORDER_STATUS_TEXT[o.status]}`}
                            >
                              {ORDER_STATUS_LABELS[o.status]}
                            </p>
                          </div>
                        </Link>
                      </li>
                    ))}
                  </ul>
                  {user.orderCount > user.recentOrders.length && (
                    <p className="mt-3 text-xs text-muted-foreground">
                      최근 {user.recentOrders.length}건만 표시 — 전체는 주문 관리에서 확인하세요.
                    </p>
                  )}
                </>
              ))}

            {tab === 'listings' &&
              (user.recentListings.length === 0 ? (
                <EmptyLine text="등록한 상품이 없어요." />
              ) : (
                <ul className="divide-y divide-border/40">
                  {user.recentListings.map((l) => (
                    <li key={l.id} className="flex items-center gap-3 py-3">
                      <p className="min-w-0 flex-1 truncate text-sm font-bold">{l.title}</p>
                      <div className="shrink-0 text-right">
                        <p className="text-sm font-bold tabular-nums">
                          {l.priceAmount.toLocaleString('ko-KR')}원
                        </p>
                        <p
                          className={`mt-0.5 text-xs font-semibold ${LISTING_STATUS_META[l.status].cls}`}
                        >
                          {LISTING_STATUS_META[l.status].label}
                        </p>
                      </div>
                    </li>
                  ))}
                </ul>
              ))}

            {tab === 'ownedSponsorships' &&
              (!ownedSponsorships ? (
                <LoadingRow />
              ) : ownedSponsorships.items.length === 0 ? (
                <EmptyLine text="등록한 협찬이 없어요." />
              ) : (
                <OwnedSponsorships
                  token={session.accessToken}
                  sponsorships={ownedSponsorships.items}
                />
              ))}

            {tab === 'sponsorships' &&
              (!applications ? (
                <LoadingRow />
              ) : applications.items.length === 0 ? (
                <EmptyLine text="협찬 지원 내역이 없어요." />
              ) : (
                <>
                  <ul className="divide-y divide-border/40">
                    {applications.items.map((a) => {
                      const meta = SPONSORSHIP_APPLICATION_STATUS_META[a.status];
                      return (
                        <li key={a.id} className="flex items-center gap-3 py-3">
                          <div className="min-w-0 flex-1">
                            <SponsorshipTitleCell a={a} />
                            <p className="mt-0.5 text-xs text-muted-foreground">
                              {formatDate(a.createdAt)} 지원
                              {a.qualifiedPlatform && ` · ${qualifiedChannelLabel(a)}`}
                              {a.visitDate && ` · 방문 ${a.visitDate}`}
                            </p>
                            {a.message && (
                              <p className="mt-1 line-clamp-2 whitespace-pre-wrap text-xs text-muted-foreground">
                                {a.message}
                              </p>
                            )}
                          </div>
                          <div className="shrink-0 text-right">
                            <p className={`text-xs font-bold ${meta.cls}`}>{meta.label}</p>
                            {a.decidedAt && (
                              <p className="mt-0.5 text-xs text-muted-foreground">
                                {formatDate(a.decidedAt)}
                              </p>
                            )}
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                  {applications.total > applications.items.length && (
                    <p className="mt-3 text-xs text-muted-foreground">
                      최근 {applications.items.length}건만 표시 — 전체는{' '}
                      <Link
                        to="/sponsorship-applications"
                        search={{ q: user.email }}
                        className="font-semibold text-brand hover:underline"
                      >
                        협찬 지원 내역
                      </Link>
                      에서 확인하세요.
                    </p>
                  )}
                </>
              ))}

            {tab === 'agreements' &&
              (user.agreements.length === 0 ? (
                <EmptyLine text="동의 이력이 없어요." />
              ) : (
                <ul className="divide-y divide-border/40">
                  {user.agreements.map((a) => (
                    <li
                      key={`${a.policySlug}-${a.version}-${a.agreedAt}`}
                      className="flex items-center gap-3 py-3"
                    >
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-sm font-bold">
                          {a.policyTitle}
                          <span className="ml-1.5 font-medium text-muted-foreground">
                            v{a.version}
                          </span>
                        </p>
                        <p className="mt-0.5 text-xs text-muted-foreground">{a.policySlug}</p>
                      </div>
                      <p className="shrink-0 text-xs text-muted-foreground">
                        {formatDateTime(a.agreedAt)}
                      </p>
                    </li>
                  ))}
                </ul>
              ))}
          </div>
        </>
      )}

      {/* ── 인증 채널 해제 다이얼로그 ── */}
      {revoking && user && (
        <PromptDialog
          title={`${SOCIAL_PLATFORM_LABELS[revoking.platform]} 채널 인증을 해제할까요?`}
          description={`${user.name} 님의 인증 기록과 공개 프로필의 채널 링크가 지워져요. 사유는 회원에게 알림으로 전달되고, 회원은 다시 인증할 수 있어요.`}
          placeholder="해제 사유"
          submitLabel="인증 해제"
          danger
          busy={busy}
          onSubmit={(reason) => void revokeChannel(revoking, reason)}
          onClose={() => setRevoking(null)}
        />
      )}

      {/* ── 정지 다이얼로그 ── */}
      {pinResetOpen && user && (
        <ConfirmDialog
          title="간편비밀번호를 초기화할까요?"
          description="고객센터에서 본인 확인을 마친 경우에만 진행해 주세요. 회원은 다음에 정산 계좌·환불 계좌를 바꾸거나 탈퇴할 때 새로 만들어야 하고, 초기화 알림이 회원에게 전송돼요."
          confirmLabel="초기화"
          danger
          busy={busy}
          onConfirm={() => void resetSimplePin()}
          onClose={() => setPinResetOpen(false)}
        />
      )}

      {suspendOpen && user && (
        <PromptDialog
          title="이용을 정지할까요?"
          description={`${user.name} 님은 정지 즉시 로그인과 토큰 재발급이 차단돼요.`}
          placeholder="정지 사유 (감사 로그에 기록)"
          submitLabel="정지"
          danger
          busy={busy}
          onSubmit={(reason) => void suspend(reason)}
          onClose={() => setSuspendOpen(false)}
        />
      )}
    </main>
  );
}

function EmptyLine({ text }: { text: string }) {
  return <p className="py-4 text-sm text-muted-foreground">{text}</p>;
}

/** 유튜브는 구독자, 그 외는 팔로워 */
function followerLabel(platform: SocialPlatform): string {
  return platform === 'YOUTUBE' ? '구독자' : '팔로워';
}

/** UUID 전체 표시 + 클릭 복사 */
function CopyableId({ value }: { value: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <button
      type="button"
      title="클릭하면 복사됩니다"
      onClick={() => {
        void navigator.clipboard.writeText(value).then(() => {
          setCopied(true);
          setTimeout(() => setCopied(false), 1500);
        });
      }}
      className="group mt-1 inline-flex items-center gap-1.5 font-mono text-xs text-muted-foreground transition hover:text-brand"
    >
      <span className="break-all text-left">{value}</span>
      <span
        className={`shrink-0 font-sans text-[11px] font-semibold ${copied ? 'text-emerald-600' : 'opacity-0 transition group-hover:opacity-100'}`}
      >
        {copied ? '복사됨' : '복사'}
      </span>
    </button>
  );
}
